import React, { useEffect, useState } from 'react';

import { TextField, InputAdornment } from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { Element } from 'react-scroll';

import useDebounce from 'common/hooks/useDebounce';
import { numberFormat } from 'common/utils/helpers';
import {
    getCountryCurrencyLocale,
    getCountryCurrencyDecimalSeparator,
    getCountryCurrencyThousandSeparator,
} from 'common/utils/session';

const getFormattedPlaceholder = (placeholder, currencyLocale) => {
    if (!placeholder && placeholder !== 0) return '';

    const parsedPlaceholder = Number(placeholder);

    if (Number.isNaN(parsedPlaceholder)) return placeholder;

    return numberFormat(parsedPlaceholder, {
        decimals: 0,
        locale: currencyLocale,
        style: 'decimal',
    });
};

const handleValue = (e, decimalSeparator, thousandSeparator) => {
    let newValue = '';
    if (e.target.value) {
        const [integerPart, decimalPart] =
            e.target.value.split(decimalSeparator);
        let cleanedIntegerPart = '0';
        if (integerPart)
            cleanedIntegerPart = integerPart.replaceAll(thousandSeparator, '');
        let cleanedNumber = `${parseInt(cleanedIntegerPart)}`;
        if (decimalPart) cleanedNumber = `${cleanedNumber}.${decimalPart}`;
        newValue = cleanedNumber;
    }
    return newValue;
};

const ReactHookFormIntlNumberInputConsumption = (props) => {
    const {
        allowDecimals = true,
        allowNegativeValue = false,
        append,
        autoFocus = false,
        control,
        dataIntercomTarget,
        debounceTime = 0,
        decimalsLimit = 16,
        disabled,
        fullWidth,
        label,
        max,
        min,
        name,
        onBlur,
        onChange,
        onPaste,
        prepend,
        readOnly,
        sx,
        variant,
    } = props;
    const [event, setEvent] = useState({});
    const debouncedValue = useDebounce(event, debounceTime);

    const countryCurrencyLocale = getCountryCurrencyLocale();
    const thousandSeparator = getCountryCurrencyThousandSeparator();
    const decimalSeparator =
        getCountryCurrencyDecimalSeparator(thousandSeparator);

    const getIsAllowedValue = ({ floatValue }) => {
        const _max = parseFloat(max);
        const _min = parseFloat(min);
        return (_min && !isNaN(_min) && floatValue < _min) ||
            (_max && !isNaN(_max) && floatValue > _max)
            ? false
            : true;
    };
    const onChangeAction = (event) =>
        setEvent({
            ...event,
            target: {
                ...event.target,
                value: handleValue(event, decimalSeparator, thousandSeparator),
            },
        });

    const onBlurAction = (event, onBlur) => {
        const value = handleValue(event, decimalSeparator, thousandSeparator);
        if (onBlur) onBlur(event, value);
    };

    const onPasteAction = (event, onPaste) => {
        const value = handleValue(event, decimalSeparator, thousandSeparator);
        if (onPaste) onPaste(event, value);
    };

    useEffect(() => {
        if (onChange && !isEmpty(event)) return onChange(event);
    }, [debouncedValue]);

    return (
        <>
            {name && <Element name={`position-${name}`} />}
            <Controller
                control={control}
                name={name}
                render={({ field, fieldState: { error } }) => (
                    <NumericFormat
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            endAdornment: append && (
                                <InputAdornment position="end">
                                    {append}
                                </InputAdornment>
                            ),
                            readOnly: readOnly,
                            startAdornment: prepend && (
                                <InputAdornment position="start">
                                    {prepend}
                                </InputAdornment>
                            ),
                            inputRef: field.ref,
                        }}
                        allowNegative={allowNegativeValue}
                        autoFocus={autoFocus}
                        customInput={TextField}
                        data-intercom-target={dataIntercomTarget}
                        decimalScale={!allowDecimals ? 0 : decimalsLimit}
                        decimalSeparator={decimalSeparator}
                        disabled={disabled}
                        error={Boolean(error)}
                        fullWidth={fullWidth}
                        helperText={error?.value?.message}
                        isAllowed={getIsAllowedValue}
                        label={label ?? field?.value?.label}
                        name={field.name}
                        onBlur={(e) => onBlurAction(e, onBlur)}
                        onChange={onChangeAction}
                        onPaste={(e) => onPasteAction(e, onPaste)}
                        onValueChange={(target) =>
                            field.onChange({
                                capture_mode: field.value?.capture_mode,
                                label: field?.value?.label || label,
                                placeholder:
                                    field.value?.placeholder || target.value,
                                tier: field.value?.tier,
                                value: target.floatValue ?? null,
                            })
                        }
                        placeholder={getFormattedPlaceholder(
                            field.value?.placeholder,
                            countryCurrencyLocale,
                        )}
                        sx={sx}
                        thousandSeparator={thousandSeparator}
                        value={field.value?.value ?? null}
                        variant={variant}
                    />
                )}
            />
        </>
    );
};

ReactHookFormIntlNumberInputConsumption.propTypes = {
    allowDecimals: PropTypes.bool,
    allowNegativeValue: PropTypes.bool,
    append: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    autoFocus: PropTypes.bool,
    control: PropTypes.object,
    dataIntercomTarget: PropTypes.string,
    debounceTime: PropTypes.number,
    decimalsLimit: PropTypes.number,
    disabled: PropTypes.bool,
    fullWidth: PropTypes.bool,
    label: PropTypes.string,
    max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onPaste: PropTypes.func,
    prepend: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.node,
    ]),
    readOnly: PropTypes.bool,
    sx: PropTypes.object,
    variant: PropTypes.string,
};

export default ReactHookFormIntlNumberInputConsumption;
